import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { ApiError } from "./api/client";
import { useAuthStore } from "./stores/auth";
import { initializeTheme } from "./stores/theme";
import "./index.css";

initializeTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Market data is the dominant read and it does not change while a session is
      // closed, so a short stale window plus no refetch-on-focus keeps the app quiet.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 4xx cannot help: the request is wrong, not unlucky. Retrying a
        // 503 can, because the lake may still be syncing.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

// Restore the refresh token before the first render, so a reload does not flash the
// login screen for an already-authenticated user.
void useAuthStore.getState().hydrate();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
